import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowUpRight } from "lucide-react";
import { formatMoney, formatDeadline, daysUntil } from "@/lib/mockData";
import { useApp } from "@/context/AppContext";
import MatchRing from "./MatchRing";
import { cn } from "@/lib/utils";

export default function GrantCard({ grant, compact = false }) {
    const { applications } = useApp();
    const existing = applications.find((a) => a.target_id === grant.id);
    const days = daysUntil(grant.deadline);
    const urgent = days !== null && days <= 30;

    return (
        <Card className={cn("group flex flex-col h-full p-6 border-border hover:border-foreground/30 transition-colors shadow-soft relative overflow-hidden", compact && "p-5") }>
            <div aria-hidden className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-accent/5 group-hover:bg-accent/10 transition-colors" />
            <div className="flex items-start justify-between gap-3 relative">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono uppercase tracking-wider">{grant.orgShort}</span>
                        <span>·</span>
                        <span className="capitalize">{grant.category}</span>
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl leading-tight mt-2 text-balance text-foreground">
                        {grant.title}
                    </h3>
                </div>
                <MatchRing score={grant.matchScore} size={compact ? 44 : 52} />
            </div>

            {!compact && <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-2">{grant.description}</p>}

            <div className="mt-5 flex items-end justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Award up to</p>
                    <p className="font-serif text-3xl text-foreground">{formatMoney(grant.amount, { compact: grant.amount >= 100000 })}</p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Deadline</p>
                    <div className="flex items-center gap-1.5 justify-end mt-0.5">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className={cn("text-sm font-medium", urgent ? "text-terracotta" : "text-foreground")}>
                            {formatDeadline(grant.deadline)}
                        </span>
                    </div>
                    {days !== null && <p className={cn("text-xs mt-0.5", urgent ? "text-terracotta" : "text-muted-foreground")}>{days} days left</p>}
                </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-1.5">
                {grant.tags.slice(0, 3).map((t) => (<Badge key={t} variant="outline" className="bg-secondary/60 border-border text-xs font-normal">{t}</Badge>))}
                <Badge variant="outline" className={cn("text-xs font-normal border-border", grant.difficulty === "Easy" ? "bg-success/10 text-success" : grant.difficulty === "Medium" ? "bg-warning/10 text-warning" : "bg-terracotta/10 text-terracotta")}>{grant.difficulty}</Badge>
            </div>

            <div className="mt-6 flex items-center gap-2 pt-4 border-t border-border/70 mt-auto">
                <Button asChild className="flex-1 rounded-full bg-foreground text-background hover:bg-foreground/90">
                    <Link to={`/app/detail/grant/${grant.id}`}>
                        {existing ? "Resume application" : "Start application"}
                        <ArrowUpRight className="h-4 w-4 ml-1" />
                    </Link>
                </Button>
            </div>
        </Card>
    );
}
